import { Info, Add } from "@mui/icons-material";
import {
  Container,
  Card,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Tooltip,
  Button,
} from "@mui/material";
import { useCustom } from "@refinedev/core";
import { useState, useEffect, useMemo } from "react";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { DefaultRuleRow } from "./components/DefaultRuleRow";
import { NewRuleRow } from "./components/NewRuleRow";
import { PricingSettingRow } from "./components/PriceSettingRow/PricingSettingRow";
import { SoleSellerSettingRow } from "./components/SoleSellerSettingRow";
import { useTranslation } from "../../../hooks/useTranslation";
import { SyncrosaleLoader } from "../../../components/layout/components/SyncrosaleLoader";

export type SoleSellerSetting = {
  additionalSoleSellerPercentage: number;
  useSoleSellerPercentage: boolean;
};

export type PricingRule = {
  id: string;
  sellerId: string;
  marketplaceId: number;
  lowerBound: number;
  upperBound: number;
  minProfitPercentage: number;
  standardProfitPercentage: number;
  maxProfitPercentage: number;
  gap?: boolean;
};

export const PricingSettings = () => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation("pages.stores.pricingSettings");

  const [editingRuleId, setEditingRuleId] = useState<string | undefined>();

  const { data: pricingRules, isLoading: pricingRulesLoading } = useCustom<
    PricingRule[]
  >({
    url: `store/${selectedCountry?.id}/price-rules`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
    },
  });

  // Reset editing rule id when country changes.
  useEffect(() => {
    setEditingRuleId(undefined);
  }, [selectedCountry]);

  const { data: defaultRule, isLoading: defaultRulesLoading } = useCustom<
    PricingRule & SoleSellerSetting
  >({
    url: `store/${selectedCountry?.id}/price-settings`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
    },
  });

  const generateUniqueId = (): string => {
    return window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36);
  };

  // Adds missing ranges between pricing rules as gap rules
  const combinedRules = useMemo(() => {
    if (!pricingRules || !defaultRule) return [];

    const combined: PricingRule[] = [];
    let lastUpperBound: number = 0;

    pricingRules.data?.forEach((rule) => {
      const { lowerBound, upperBound } = rule;

      if (lastUpperBound && lowerBound > lastUpperBound) {
        combined.push({
          ...defaultRule?.data,
          id: generateUniqueId(),
          lowerBound: lastUpperBound,
          upperBound: lowerBound,
          gap: true,
        });
      }

      combined.push(rule);

      lastUpperBound = upperBound;
    });

    return combined;
  }, [pricingRules, defaultRule]);

  if (defaultRulesLoading || pricingRulesLoading) {
    return <SyncrosaleLoader />;
  }

  return (
    <Container
      component={Card}
      sx={{
        padding: "8px",
      }}
      maxWidth={false}
    >
      <Typography variant="h6" textAlign="center" marginTop="8px">
        {translate("title")}
      </Typography>

      <SoleSellerSettingRow defaultValues={defaultRule!.data} />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.customBackground.box,
            }}
          >
            <TableRow>
              <TableCell align="center">{translate("fields.cost")}</TableCell>
              <TableCell align="center">
                {translate("fields.minProfitPercentage")}
              </TableCell>
              <TableCell align="center">
                {translate("fields.standardProfitPercentage")}
              </TableCell>
              <TableCell align="center">
                {translate("fields.maxProfitPercentage")}
              </TableCell>
              <TableCell align="center" width="150px">
                {translate("fields.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DefaultRuleRow
              editingRuleId={editingRuleId}
              setEditingRuleId={setEditingRuleId}
              defaultRule={defaultRule!.data}
            />
            {combinedRules.map((item, index) => (
              <PricingSettingRow
                key={item.id}
                rule={item}
                isEditing={editingRuleId === item.id}
                onEditStart={() => setEditingRuleId(item.id)}
                url={
                  item.gap
                    ? `store/${selectedCountry?.id}/price-rules`
                    : `store/${selectedCountry?.id}/price-rules/${item.id}`
                }
                method={item.gap ? "post" : "put"}
                deletable={!item.gap}
                customHeader={
                  // Show an info tooltip next to gap rules.
                  item.gap ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography fontWeight="bold">
                        $ {item.lowerBound ?? "~"} - $ {item.upperBound ?? "~"}
                      </Typography>
                      <Tooltip title={translate("gap.info")}>
                        <Info
                          fontSize="small"
                          style={{ marginLeft: 4 }}
                          color="primary"
                        />
                      </Tooltip>
                    </Box>
                  ) : null
                }
                onEditStop={(data) => {
                  // If data is not null, it means user saved new values.

                  if (data !== undefined) {
                    combinedRules[index] = data;
                  }
                  setEditingRuleId(undefined);
                }}
                showEdit={
                  editingRuleId === undefined || editingRuleId === item.id
                }
              />
            ))}

            {editingRuleId === "newRule" && (
              <NewRuleRow onClose={() => setEditingRuleId(undefined)} />
            )}
            <TableRow>
              <TableCell colSpan={7} padding="none">
                <Button
                  onClick={() => setEditingRuleId("newRule")}
                  variant="text"
                  disableElevation
                  size="large"
                  disabled={editingRuleId === "newRule"}
                  startIcon={<Add />}
                  sx={{ borderRadius: "0px" }}
                  fullWidth
                >
                  {translate("addRange")}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};
